/**
 * Webhooks feature — Zod schemas.
 *
 * Webhooks are tenant-scoped HTTP endpoints PureID delivers events
 * to. Each webhook has a signing secret that's generated by the
 * server and revealed to the UI exactly once — on create and on
 * explicit rotation. All other reads return the secret as null.
 *
 * The `events` array is a whitelist of event names (e.g.
 * `user.created`, `passkey.revoked`). The server validates it; the
 * frontend does not hard-code the list because new events land
 * independently of UI releases.
 */

import { z } from "zod";

// ── Enums ─────────────────────────────────────────────────────────────────

export const webhookHealthSchema = z.enum(["healthy", "degraded", "failing", "unknown"]);
export type WebhookHealth = z.infer<typeof webhookHealthSchema>;

export const deliveryStatusSchema = z.enum([
  "pending",
  "delivered",
  "failed",
  "abandoned",
]);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

// ── List filter ───────────────────────────────────────────────────────────

export const webhooksListSearchSchema = z.object({
  q: z.string().optional(),
  enabled: z.coerce.boolean().optional(),
});
export type WebhooksListSearch = z.infer<typeof webhooksListSearchSchema>;

// ── Webhook record ────────────────────────────────────────────────────────

export const webhookSchema = z.object({
  id: z.string(),
  url: z.string(),
  description: z.string().nullable(),
  events: z.array(z.string()),
  enabled: z.boolean(),
  health: webhookHealthSchema,
  created_at: z.string(),
  updated_at: z.string(),
  last_delivery_at: z.string().nullable(),
  last_delivery_status: deliveryStatusSchema.nullable(),
  /**
   * The HMAC signing secret is null for every read path and
   * non-null only on create / rotate responses. UI reveals it once,
   * after which the operator must store it themselves.
   */
  secret: z.string().nullable(),
});
export type Webhook = z.infer<typeof webhookSchema>;

export const webhooksListResponseSchema = z.object({
  data: z.array(webhookSchema),
});
export type WebhooksListResponse = z.infer<typeof webhooksListResponseSchema>;

// ── Delivery record ───────────────────────────────────────────────────────

export const webhookDeliverySchema = z.object({
  id: z.string(),
  webhook_id: z.string(),
  event: z.string(),
  status: deliveryStatusSchema,
  status_code: z.number().int().nullable(),
  attempt: z.number().int().nonnegative(),
  duration_ms: z.number().int().nullable(),
  delivered_at: z.string().nullable(),
  error: z.string().nullable(),
  request_body: z.string().nullable(),
  response_body: z.string().nullable(),
  created_at: z.string(),
});
export type WebhookDelivery = z.infer<typeof webhookDeliverySchema>;

export const webhookDeliveriesResponseSchema = z.object({
  data: z.array(webhookDeliverySchema),
});

// ── Mutation inputs ───────────────────────────────────────────────────────

const httpsUrl = z
  .string()
  .url("Must be a valid URL")
  .refine((v) => v.startsWith("https://"), "Webhook URLs must use HTTPS");

export const createWebhookSchema = z.object({
  url: httpsUrl,
  description: z.string().max(200).optional(),
  events: z.array(z.string().min(1)).min(1, "Select at least one event"),
  enabled: z.boolean(),
});
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>;

export const updateWebhookSchema = z.object({
  url: httpsUrl.optional(),
  description: z.string().max(200).nullable().optional(),
  events: z.array(z.string().min(1)).min(1).optional(),
  enabled: z.boolean().optional(),
});
export type UpdateWebhookInput = z.infer<typeof updateWebhookSchema>;
